import { createSlice, type PayloadAction, createAction } from '@reduxjs/toolkit'
import { type IDeviceStatusRegisterData } from '../services/getDeviceStatusSummaryService'

export interface IDeviceRegisterStatusState {
  isLoading: boolean
  error: string | undefined
  data: any | null
  httpStatus: number | undefined
}

export const initialState: IDeviceRegisterStatusState = {
  isLoading: false,
  error: undefined,
  data: null,
  httpStatus: 0
}

const deviceRegisterStatusSummarySlice = createSlice({
  name: 'DeviceRegisterStatusSummayState',
  initialState,
  reducers: {
    deviceRegisterStatusSummayLoadingAction: (state) => {
      return {
        isLoading: true,
        error: undefined,
        data: null,
        httpStatus: 0
      }
    },
    deviceRegisterStatusSummayUpdateAction: (state, action: PayloadAction<{ data: IDeviceStatusRegisterData, httpStatus: number }>) => {
      return {
        isLoading: false,
        error: undefined,
        data: action.payload.data,
        httpStatus: action.payload.httpStatus
      }
    },
    deviceRegisterStatusSummayErrorAction: (state, action: PayloadAction<{ errorMessage: string, httpStatus: number | undefined }>) => {
      return {
        isLoading: false,
        error: action.payload.errorMessage,
        data: null,
        httpStatus: action.payload.httpStatus
      }
    },
    updateRegisterReadingsAction: (state, action: PayloadAction<{ deviceId: string, registerReadings: any[] }>) => {
      console.log('WebSocket register update:', action.payload)

      // Only update if we have existing data
      if (Boolean(state.data?.registerReadings) && Array.isArray(state.data.registerReadings)) {
        const updatedRegisterReadings = [...state.data.registerReadings]

        // Update existing readings with new values from WebSocket
        action.payload.registerReadings.forEach((newReading) => {
          const existingIndex = updatedRegisterReadings.findIndex(
            (existing) => existing.cimCodeId === newReading.cimCodeId
          )

          if (existingIndex !== -1) {
            // Update the existing reading with new values, preserving the structure
            updatedRegisterReadings[existingIndex] = {
              ...updatedRegisterReadings[existingIndex],
              value: newReading.value,
              formattedValue: newReading.formattedValue,
              colorValue: newReading.colorValue,
              readingDateTime: newReading.readingDateTime,
              readingTime: newReading.readingTime,
              indexValue: newReading.indexValue,
              units: newReading.units,
              registerName: newReading.registerName
            }
          } else {
            // If the register doesn't exist in current data, add it
            // This handles the case where user just selected a new register
            console.log('Adding new register reading:', newReading.cimCodeId)
            updatedRegisterReadings.push({
              value: newReading.value,
              formattedValue: newReading.formattedValue,
              colorValue: newReading.colorValue,
              readingDateTime: newReading.readingDateTime,
              readingTime: newReading.readingTime ?? new Date().toISOString(),
              indexValue: newReading.indexValue,
              units: newReading.units,
              registerName: newReading.registerName,
              cimCodeId: newReading.cimCodeId,
              // Add default values for missing properties
              execution_status: newReading.execution_status ?? null
            })
          }
        })

        console.log('Updated register readings:', updatedRegisterReadings)

        return {
          ...state,
          data: {
            ...state.data,
            registerReadings: updatedRegisterReadings
          }
        }
      } else {
        // If no existing data, create initial state with the new readings
        console.log('No existing data, creating initial state with WebSocket data')
        return {
          ...state,
          data: {
            registerReadings: action.payload.registerReadings.map(reading => ({
              ...reading,
              readingTime: reading.readingTime ?? new Date().toISOString(),
              execution_status: reading.execution_status ?? null
            }))
          }
        }
      }
    }
  }
})

// For register readings
export const updateRegisterReadings = createAction<{ deviceId: string, registerReadings: any[] }>('deviceStatus/updateRegisterReadings')

// For KPI data
export const updateKPIData = createAction<{ deviceId: string, kpi_data: any[] }>('deviceStatus/updateKPIData')

export const {
  deviceRegisterStatusSummayLoadingAction,
  deviceRegisterStatusSummayUpdateAction,
  deviceRegisterStatusSummayErrorAction,
  updateRegisterReadingsAction
} = deviceRegisterStatusSummarySlice.actions

export default deviceRegisterStatusSummarySlice.reducer
